// Gladiator entity ported from Java Gladiator.java

import { Race } from '../data/races';
import { Item, ITEM_TYPE, WEAPON_SUBTYPE } from '../data/items';
import { Spell, SpellType, SPELL_TYPE } from '../data/spells';
import { GAME_CONSTANTS } from '../data/constants';
import { rollDiceStr, randomRange } from '../utils/dice';
import { getRandomGladiatorName } from '../data/names';

export interface Position {
  x: number;
  y: number;
}

// Active spell effect on a gladiator
export interface ActiveEffect {
  type: SpellType;
  value: number;       // Strength of the effect
  remainingTurns: number;
}

export interface GladiatorSkills {
  // Melee attack skills (0=unarmed, 1=spear, 2=sword, 3=axe, 4=mace)
  meleeAtk: [number, number, number, number, number];
  // Melee defense skills
  meleeDef: [number, number, number, number, number];
  // Ranged attack skills (0=bow, 1=crossbow)
  rangedAtk: [number, number];
  // Abilities (0=evasion, 1=magic resist)
  abilities: [number, number];
  // Spell attack skills (one per known spell slot)
  spellAtk: [number, number, number, number];
}

export interface SkillUpgrades {
  meleeAtk: [number, number, number, number, number];
  meleeDef: [number, number, number, number, number];
  rangedAtk: [number, number];
  abilities: [number, number];
  spellAtk: [number, number, number, number];
}

export interface GladiatorData {
  id: string;
  name: string;
  race: Race;
  age: number;
  salary: number;

  // Base stats
  str: number;
  speed: number;
  hp: number;
  maxHp: number;
  mana: number;
  maxMana: number;

  // Bonuses
  strBonus: number;
  speedBonus: number;
  manaBonus: number;

  // Skills
  skills: GladiatorSkills;

  // Equipment slots: [melee, ranged, armor]
  items: [Item | null, Item | null, Item | null];

  // Known spells (max 4)
  knownSpells: [Spell | null, Spell | null, Spell | null, Spell | null];

  // Status
  hurt: number;      // Months until recovered
  matches: number;   // Total matches played
  takeOuts1: number; // KOs this year
  takeOuts2: number; // KOs total

  // Battle state
  position: Position | null;
  receivedDmg: number;
  ammo: number;
  needsReload: boolean;
  hasGainedLevel: boolean;
  skillUpgrades: SkillUpgrades;

  // Active spell effects
  activeEffects: ActiveEffect[];
  stunTurns: number;     // Skip turns (from MYSTIFICATION)
  rootedTurns: number;   // Cannot move (from STOP)

  // Auction state
  askPrice: number;
  offerPrice: number;
  offerTeamId: string | null;

  // Team reference
  ownerId: string | null;
  isSpecial: boolean;

  // Training history - tracks times each stat has been trained
  trainingHistory: {
    str: number;
    speed: number;
    maxHp: number;
    maxMana: number;
  };
}

// Generate a new random gladiator
export function createGladiator(race: Race, id?: string): GladiatorData {
  const name = getRandomGladiatorName();
  const gladiatorId = id ?? `gladiator-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Roll base stats from race
  const str = rollDiceStr(race.str);
  const speed = rollDiceStr(race.speed);
  const maxMana = rollDiceStr(race.mana);
  const maxHp = rollDiceStr(race.health);

  // Random age between min and max
  const age = randomRange(GAME_CONSTANTS.GLADIATOR_MIN_AGE, GAME_CONSTANTS.GLADIATOR_MAX_AGE);

  // Generate random skills (3-52 range like original)
  const skills: GladiatorSkills = {
    meleeAtk: [
      randomRange(3, 52),
      randomRange(3, 52),
      randomRange(3, 52),
      randomRange(3, 52),
      randomRange(3, 52),
    ],
    meleeDef: [
      10, // Default unarmed defense
      randomRange(3, 52),
      randomRange(3, 52),
      randomRange(3, 52),
      randomRange(3, 52),
    ],
    rangedAtk: [randomRange(3, 52), randomRange(3, 52)],
    abilities: [randomRange(3, 52), randomRange(3, 52)],
    spellAtk: [0, 0, 0, 0], // Set when spells are learned
  };

  // Initialize spell attack to magic resist value
  const magicResist = skills.abilities[1];
  skills.spellAtk = [magicResist, magicResist, magicResist, magicResist];

  return {
    id: gladiatorId,
    name,
    race,
    age,
    salary: randomRange(6, 65),
    str,
    speed,
    hp: maxHp,
    maxHp,
    mana: maxMana,
    maxMana,
    strBonus: randomRange(-3, 8),
    speedBonus: randomRange(-9, 9),
    manaBonus: randomRange(-9, 11),
    skills,
    items: [null, null, null],
    knownSpells: [null, null, null, null],
    hurt: 0,
    matches: 0,
    takeOuts1: 0,
    takeOuts2: 0,
    position: null,
    receivedDmg: -1,
    ammo: GAME_CONSTANTS.AMMO_COUNT,
    needsReload: false,
    hasGainedLevel: false,
    skillUpgrades: {
      meleeAtk: [0, 0, 0, 0, 0],
      meleeDef: [0, 0, 0, 0, 0],
      rangedAtk: [0, 0],
      abilities: [0, 0],
      spellAtk: [0, 0, 0, 0],
    },
    activeEffects: [],
    stunTurns: 0,
    rootedTurns: 0,
    askPrice: 50,
    offerPrice: 0,
    offerTeamId: null,
    ownerId: null,
    isSpecial: false,
    trainingHistory: {
      str: 0,
      speed: 0,
      maxHp: 0,
      maxMana: 0,
    },
  };
}

// Get attack skill for a weapon
export function getAtkSkillForWeapon(gladiator: GladiatorData, weapon: Item | null): number {
  if (!weapon) return gladiator.skills.meleeAtk[0]; // Unarmed

  if (weapon.type === ITEM_TYPE.RANGED_WEAPON) {
    if (weapon.subtype === WEAPON_SUBTYPE.BOW) return gladiator.skills.rangedAtk[0];
    if (weapon.subtype === WEAPON_SUBTYPE.CROSSBOW) return gladiator.skills.rangedAtk[1];
  }

  if (weapon.type === ITEM_TYPE.MELEE_WEAPON) {
    switch (weapon.subtype) {
      case WEAPON_SUBTYPE.SPEAR: return gladiator.skills.meleeAtk[1];
      case WEAPON_SUBTYPE.SWORD: return gladiator.skills.meleeAtk[2];
      case WEAPON_SUBTYPE.AXE: return gladiator.skills.meleeAtk[3];
      case WEAPON_SUBTYPE.MACE: return gladiator.skills.meleeAtk[4];
    }
  }

  return gladiator.skills.meleeAtk[0];
}

// Get defense skill for a weapon
export function getDefSkillForWeapon(gladiator: GladiatorData, weapon: Item | null): number {
  if (!weapon) return gladiator.skills.meleeDef[0]; // Unarmed

  if (weapon.type === ITEM_TYPE.MELEE_WEAPON) {
    switch (weapon.subtype) {
      case WEAPON_SUBTYPE.SPEAR: return gladiator.skills.meleeDef[1];
      case WEAPON_SUBTYPE.SWORD: return gladiator.skills.meleeDef[2];
      case WEAPON_SUBTYPE.AXE: return gladiator.skills.meleeDef[3];
      case WEAPON_SUBTYPE.MACE: return gladiator.skills.meleeDef[4];
    }
  }

  return gladiator.skills.meleeDef[0];
}

// Get attack skill for a spell
export function getAtkSkillForSpell(gladiator: GladiatorData, spell: Spell): number {
  for (let i = 0; i < gladiator.knownSpells.length; i++) {
    if (gladiator.knownSpells[i]?.id === spell.id) {
      return gladiator.skills.spellAtk[i];
    }
  }
  return 0;
}

// Equipment getters
export function getMeleeWeapon(gladiator: GladiatorData): Item | null {
  return gladiator.items[GAME_CONSTANTS.MELEE_WEAPON_SLOT];
}

export function getRangedWeapon(gladiator: GladiatorData): Item | null {
  return gladiator.items[GAME_CONSTANTS.RANGED_WEAPON_SLOT];
}

export function getArmor(gladiator: GladiatorData): Item | null {
  return gladiator.items[GAME_CONSTANTS.ARMOUR_SLOT];
}

// Get armor protection value
export function getArmorValue(gladiator: GladiatorData): number {
  const armor = getArmor(gladiator);
  return armor?.protection ?? 0;
}

// Get melee damage string
export function getMeleeDmg(gladiator: GladiatorData): string {
  const weapon = getMeleeWeapon(gladiator);
  const bonus = gladiator.strBonus;

  if (!weapon) {
    // Unarmed: 1d2 + strBonus
    if (bonus > 0) return `1d2+${bonus}`;
    if (bonus < 0) return `1d2${bonus}`;
    return '1d2';
  }

  // Parse weapon damage and add str bonus
  const dmg = weapon.dmg!;
  const match = dmg.match(/^(\d+)d(\d+)([+-]\d+)?$/);
  if (!match) return dmg;

  const count = match[1];
  const sides = match[2];
  const modifier = (match[3] ? parseInt(match[3], 10) : 0) + bonus;

  if (modifier > 0) return `${count}d${sides}+${modifier}`;
  if (modifier < 0) return `${count}d${sides}${modifier}`;
  return `${count}d${sides}`;
}

// Get ranged damage string
export function getRangedDmg(gladiator: GladiatorData): string {
  const weapon = getRangedWeapon(gladiator);
  return weapon?.dmg ?? '';
}

// Get next available spell slot
export function getNextSpellSlot(gladiator: GladiatorData): number {
  for (let i = 0; i < gladiator.knownSpells.length; i++) {
    if (gladiator.knownSpells[i] === null) return i;
  }
  return -1;
}

// Get list of known spells (non-null)
export function getKnownSpells(gladiator: GladiatorData): Spell[] {
  return gladiator.knownSpells.filter((s): s is Spell => s !== null);
}

// Check if gladiator is healthy (not injured)
export function isHealthy(gladiator: GladiatorData): boolean {
  return gladiator.hurt === 0;
}

// Check if gladiator is alive (hp > 0)
export function isAlive(gladiator: GladiatorData): boolean {
  return gladiator.hp > 0;
}

// Reset skill upgrades for new battle
export function resetSkillUpgrades(gladiator: GladiatorData): void {
  gladiator.skillUpgrades = {
    meleeAtk: [0, 0, 0, 0, 0],
    meleeDef: [0, 0, 0, 0, 0],
    rangedAtk: [0, 0],
    abilities: [0, 0],
    spellAtk: [0, 0, 0, 0],
  };
  gladiator.hasGainedLevel = false;
  // Clear all spell effects
  gladiator.activeEffects = [];
  gladiator.stunTurns = 0;
  gladiator.rootedTurns = 0;
}

// Reset for new month
export function resetForNewMonth(gladiator: GladiatorData): void {
  gladiator.hp = gladiator.maxHp;
  gladiator.mana = gladiator.maxMana;
  gladiator.needsReload = false;
  gladiator.ammo = GAME_CONSTANTS.AMMO_COUNT;
  gladiator.receivedDmg = -1;
  if (gladiator.hurt > 0) {
    gladiator.hurt -= 1;
  }
}

// Reset for new year
export function resetForNewYear(gladiator: GladiatorData): void {
  resetForNewMonth(gladiator);
  gladiator.takeOuts1 = 0;
  gladiator.age += 1;
}

// Check if gladiator should retire
export function shouldRetire(gladiator: GladiatorData): boolean {
  return gladiator.age > GAME_CONSTANTS.GLADIATOR_MAX_AGE;
}

// Add an active effect to a gladiator
export function addActiveEffect(gladiator: GladiatorData, type: SpellType, value: number, duration: number): void {
  // Remove existing effect of same type
  gladiator.activeEffects = gladiator.activeEffects.filter(e => e.type !== type);
  // Add new effect
  gladiator.activeEffects.push({ type, value, remainingTurns: duration });
}

// Remove all active effects (from REMOVE_SPELL)
export function clearActiveEffects(gladiator: GladiatorData): void {
  gladiator.activeEffects = [];
  gladiator.stunTurns = 0;
  gladiator.rootedTurns = 0;
}

// Tick down all effect durations at end of turn
export function tickEffects(gladiator: GladiatorData): void {
  // Tick down stun/root
  if (gladiator.stunTurns > 0) gladiator.stunTurns--;
  if (gladiator.rootedTurns > 0) gladiator.rootedTurns--;

  // Tick down other effects and remove expired ones
  for (let i = gladiator.activeEffects.length - 1; i >= 0; i--) {
    gladiator.activeEffects[i].remainingTurns--;
    if (gladiator.activeEffects[i].remainingTurns <= 0) {
      gladiator.activeEffects.splice(i, 1);
    }
  }
}

// Check if gladiator is stunned
export function isStunned(gladiator: GladiatorData): boolean {
  return gladiator.stunTurns > 0;
}

// Check if gladiator is rooted
export function isRooted(gladiator: GladiatorData): boolean {
  return gladiator.rootedTurns > 0;
}

// Get total armor bonus from PROTECT effects
export function getProtectionBonus(gladiator: GladiatorData): number {
  let bonus = 0;
  for (const effect of gladiator.activeEffects) {
    if (effect.type === SPELL_TYPE.PROTECT) {
      bonus += effect.value;
    }
  }
  return bonus;
}

// Get hit chance modifier from BLESSING/CURSE effects
export function getHitChanceModifier(gladiator: GladiatorData): number {
  let modifier = 0;
  for (const effect of gladiator.activeEffects) {
    if (effect.type === SPELL_TYPE.BLESSING) {
      modifier += effect.value;
    } else if (effect.type === SPELL_TYPE.CURSE) {
      modifier -= effect.value;
    }
  }
  return modifier;
}
